import { API_BASE_URL } from "../config/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  CONTACT_ROLE_OPTIONS,
  cloneAddress,
  createEmptyCustomerForm,
  createEmptyContact,
  ensurePhonePrefix,
  sanitizeCustomerPayload
} from "../utils/customerForm";
import { getContactNumberWarning, getGstinWarning } from "../utils/formValidation";
import { fetchLocationByPincode, normalizePincode } from "../utils/locationApi";

const inputClassName =
  "h-11 w-full rounded-[6px] border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
const textareaClassName =
  "w-full rounded-[6px] border border-slate-300 bg-white px-3 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const warningClassName = "mt-1 text-xs text-red-500";
const CUSTOMER_DRAFT_KEY = "genmech:create-customer-draft";

function FormCard({ title, children, action }) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:px-5">
        <h2 className="text-base font-bold text-slate-950">{title}</h2>
        {action}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function Field({ label, children, required = false, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-bold text-slate-800">
        {label}{required ? <span className="text-red-500"> *</span> : null}
      </label>
      {children}
    </div>
  );
}

function CreateCustomer() {
  const role = localStorage.getItem("role") || "";
  const isAccountsView = role === "accounts";
  const navigate = useNavigate();
  const [form, setForm] = useState(createEmptyCustomerForm);
  const [loading, setLoading] = useState(false);
  const [useBillingForWorkAddress, setUseBillingForWorkAddress] = useState(false);
  const [locationStatus, setLocationStatus] = useState({
    billingAddress: { loading: false, message: "", tone: "idle" },
    address: { loading: false, message: "", tone: "idle" }
  });
  const phoneWarning = getContactNumberWarning(form.phone);
  const gstWarning = getGstinWarning(form.gstNumber);
  const isSezSupply = String(form.gstType || "").trim().toLowerCase() === "sez_supply";

  const [groups, setGroups] = useState([]);
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/customer-groups");
        setGroups(res.data);
      } catch (err) {
        console.error("Failed to load customer groups", err);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(CUSTOMER_DRAFT_KEY);
      if (savedDraft) {
        setForm({
          ...createEmptyCustomerForm(),
          ...JSON.parse(savedDraft)
        });
      }
    } catch (error) {
      console.warn("Failed to load customer draft", error);
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "phone"
        ? ensurePhonePrefix(value)
        : name === "gstType"
          ? value
          : value,
      ...(name === "gstType" && value !== "sez_supply" ? { lutRefNo: "" } : {})
    }));
  };

  const handleAddressChange = (section, event) => {
    const { name, value } = event.target;
    const nextValue = name === "pincode" ? normalizePincode(value) : value;

    setForm((current) => {
      const nextSection = {
        ...current[section],
        [name]: nextValue
      };

      return {
        ...current,
        [section]: nextSection,
        ...(section === "billingAddress" && useBillingForWorkAddress
          ? { address: cloneAddress(nextSection) }
          : {})
      };
    });

    if (name === "pincode") {
      setLocationStatus((current) => ({
        ...current,
        [section]:
          nextValue.length === 6
            ? current[section]
            : { loading: false, message: "", tone: "idle" }
      }));
    }
  };

  const handlePincodeBlur = async (section) => {
    const pincode = normalizePincode(form[section]?.pincode);

    if (!pincode) {
      setLocationStatus((current) => ({
        ...current,
        [section]: { loading: false, message: "", tone: "idle" }
      }));
      return;
    }

    if (pincode.length !== 6) {
      setLocationStatus((current) => ({
        ...current,
        [section]: { loading: false, message: "Enter a valid 6-digit pincode.", tone: "error" }
      }));
      return;
    }

    setLocationStatus((current) => ({
      ...current,
      [section]: { loading: true, message: "Looking up city and state...", tone: "loading" }
    }));

    try {
      const location = await fetchLocationByPincode(pincode);

      setForm((current) => {
        const nextSection = {
          ...current[section],
          city: location.city || current[section].city,
          state: location.state || current[section].state
        };

        return {
          ...current,
          [section]: nextSection,
          ...(section === "billingAddress" && useBillingForWorkAddress
            ? { address: cloneAddress(nextSection) }
            : {})
        };
      });

      setLocationStatus((current) => ({
        ...current,
        [section]: {
          loading: false,
          message: "City and state updated from pincode.",
          tone: "success"
        }
      }));
    } catch (error) {
      setLocationStatus((current) => ({
        ...current,
        [section]: {
          loading: false,
          message: error.response?.data?.message || error.message || "Unable to fetch location.",
          tone: "error"
        }
      }));
    }
  };

  const getLocationMessageClassName = (tone) => {
    if (tone === "error") {
      return warningClassName;
    }

    if (tone === "success") {
      return "mt-1 text-xs text-emerald-600";
    }

    return "mt-1 text-xs text-slate-500";
  };

  const handleAdditionalChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      additionalContacts: current.additionalContacts.map((contact, contactIndex) => {
        if (contactIndex !== index) {
          return field === "isPrimary"
            ? { ...contact, isPrimary: false }
            : contact;
        }

        return {
          ...contact,
          [field]:
            field === "mobileNumber" || field === "whatsappNumber"
              ? ensurePhonePrefix(value)
              : field === "isPrimary"
                ? Boolean(value)
                : value,
          isPrimary: field === "isPrimary" ? Boolean(value) : contact.isPrimary
        };
      })
    }));
  };

  const addContact = () => {
    setForm((current) => ({
      ...current,
      additionalContacts: [
        ...current.additionalContacts,
        createEmptyContact(!current.additionalContacts.some((contact) => contact.isPrimary))
      ]
    }));
  };

  const removeContact = (index) => {
    setForm((current) => ({
      ...current,
      additionalContacts:
        current.additionalContacts.length === 1
          ? [createEmptyContact(true)]
          : current.additionalContacts
              .filter((_, contactIndex) => contactIndex !== index)
              .map((contact, contactIndex) => ({
                ...contact,
                isPrimary: contactIndex === 0
              }))
    }));
  };

  const handleUseBillingForWorkAddressChange = (event) => {
    const checked = event.target.checked;
    setUseBillingForWorkAddress(checked);

    if (checked) {
      setForm((current) => ({
        ...current,
        address: cloneAddress(current.billingAddress)
      }));

      setLocationStatus((current) => ({
        ...current,
        address: { ...current.billingAddress }
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      let groupId = form.groupId;

      if (showNewGroupInput && newGroupName.trim()) {
        const groupRes = await axios.post(API_BASE_URL + "/api/customer-groups", {
          groupName: newGroupName.trim()
        });
        groupId = groupRes.data._id;
      }

      const payload = {
        ...sanitizeCustomerPayload(form),
        groupId: groupId || null
      };

      await axios.post(API_BASE_URL + "/api/customers", payload);
      localStorage.removeItem(CUSTOMER_DRAFT_KEY);
      alert("Customer created successfully.");
      navigate(isAccountsView ? "/accounts/customers" : "/customers");
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Error creating customer");
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = () => {
    localStorage.setItem(CUSTOMER_DRAFT_KEY, JSON.stringify(form));
    alert("Customer draft saved on this device.");
  };

  const cancelForm = () => {
    navigate(isAccountsView ? "/accounts/customers" : "/customers");
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="border-b border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl items-start gap-4">
          <button
            type="button"
            onClick={cancelForm}
            className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50"
            aria-label="Back to customers"
          >
            <span className="text-xl leading-none">&larr;</span>
          </button>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">Customer Management</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">Add New Customer</h1>
            <p className="mt-1 text-sm text-slate-500">Create a customer profile and manage customer details.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mx-auto max-w-7xl space-y-5 px-4 py-5 sm:px-6 lg:px-8">
        <FormCard title="Primary Details">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Customer Name" required>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Enter customer name"
                  required
                />
            </Field>

            <Field label="Contact Person">
                <input
                  name="contactPerson"
                  value={form.contactPerson}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Primary contact person"
                />
            </Field>

            <Field label="Phone / Telephone" required>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Mobile, landline, or telephone number"
                    required
                  />
                  {phoneWarning ? <p className={warningClassName}>{phoneWarning}</p> : null}
            </Field>

            <Field label="Email">
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Email address"
                  />
            </Field>

            <Field label="GST Type">
                  <select
                    name="gstType"
                    value={form.gstType}
                    onChange={handleChange}
                    className={inputClassName}
                  >
                    <option value="cgst_sgst">GST</option>
                    <option value="igst">IGST</option>
                    <option value="sez_supply">SEZ SUPPLY</option>
                  </select>
            </Field>

            <Field label="GST No">
                  <input
                    name="gstNumber"
                    value={form.gstNumber}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="GST number"
                  />
                  {gstWarning ? <p className={warningClassName}>{gstWarning}</p> : null}
            </Field>

            <Field label="Supplier Code">
                  <input
                    name="supplierCode"
                    value={form.supplierCode}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Supplier code"
                  />
            </Field>

            <Field label="Google Maps Location Link">
                  <input
                    name="googleMapsLink"
                    value={form.googleMapsLink || ""}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="E.g., https://goo.gl/maps/... or https://maps.google.com/..."
                  />
            </Field>

            {isSezSupply ? (
              <Field label="LUT Ref No" className="md:col-span-2">
                    <input
                      name="lutRefNo"
                      value={form.lutRefNo}
                      onChange={handleChange}
                      className={inputClassName}
                      placeholder="Enter LUT reference number"
                    />
              </Field>
            ) : null}

            <Field label="Customer Group">
              <select
                name="groupId"
                value={showNewGroupInput ? "__new__" : (form.groupId || "")}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "__new__") {
                    setShowNewGroupInput(true);
                  } else {
                    setShowNewGroupInput(false);
                    setForm(curr => ({ ...curr, groupId: val }));
                  }
                }}
                className={inputClassName}
              >
                <option value="">None (Ungrouped)</option>
                {groups.map((g) => (
                  <option key={g._id} value={g._id}>
                    {g.groupName}
                  </option>
                ))}
                <option value="__new__">+ Create New Group...</option>
              </select>
            </Field>

            {showNewGroupInput ? (
              <Field label="New Group Name" required>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className={inputClassName}
                  placeholder="Enter new group name"
                  required
                />
              </Field>
            ) : null}
          </div>
        </FormCard>

        <FormCard title="Billing Address">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Street Address" className="md:col-span-2">
                <input
                  name="street"
                  value={form.billingAddress.street}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="Street address"
                />
            </Field>

            <Field label="Pincode">
                <input
                  name="pincode"
                  value={form.billingAddress.pincode}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  onBlur={() => handlePincodeBlur("billingAddress")}
                  className={inputClassName}
                  placeholder="Pincode"
                />
                {locationStatus.billingAddress.message ? (
                  <p className={getLocationMessageClassName(locationStatus.billingAddress.tone)}>
                    {locationStatus.billingAddress.message}
                  </p>
                ) : null}
            </Field>

            <Field label="City">
                <input
                  name="city"
                  value={form.billingAddress.city}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="City"
                />
            </Field>

            <Field label="State">
                <input
                  name="state"
                  value={form.billingAddress.state}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="State"
                />
            </Field>
          </div>

          <label className="mt-5 flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={useBillingForWorkAddress}
                onChange={handleUseBillingForWorkAddressChange}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Use the same address for work / delivery address
          </label>
        </FormCard>

        <FormCard title="Work / Delivery Address">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Street Address" className="md:col-span-2">
                <input
                  name="street"
                  value={form.address.street}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="Street address"
                />
            </Field>

            <Field label="Pincode">
                <input
                  name="pincode"
                  value={form.address.pincode}
                  onChange={(event) => handleAddressChange("address", event)}
                  onBlur={() => handlePincodeBlur("address")}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="Pincode"
                />
                {locationStatus.address.message ? (
                  <p className={getLocationMessageClassName(locationStatus.address.tone)}>
                    {locationStatus.address.message}
                  </p>
                ) : null}
            </Field>

            <Field label="City">
                <input
                  name="city"
                  value={form.address.city}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="City"
                />
            </Field>

            <Field label="State">
                <input
                  name="state"
                  value={form.address.state}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="State"
                />
            </Field>
          </div>
        </FormCard>

        <FormCard
          title="Additional Contacts"
          action={(
            <button
              type="button"
              onClick={addContact}
              className="rounded-[6px] bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700 transition hover:bg-emerald-100"
            >
              + Add Contact
            </button>
          )}
        >
            <div className="space-y-3">
              {form.additionalContacts.map((contact, index) => (
                <div
                  key={`contact-${index}`}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[1.1fr,1.25fr,1.1fr,1.25fr,1.1fr,0.7fr,auto] xl:items-end">
                      <Field label="Role">
                        <select
                          value={contact.role}
                          onChange={(event) => handleAdditionalChange(index, "role", event.target.value)}
                          className={inputClassName}
                        >
                          {CONTACT_ROLE_OPTIONS.map((roleOption) => (
                            <option key={roleOption} value={roleOption}>
                              {roleOption}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Contact Person Name">
                        <input
                          placeholder="Contact person name"
                          value={contact.name}
                          onChange={(event) => handleAdditionalChange(index, "name", event.target.value)}
                          className={inputClassName}
                        />
                      </Field>
                      <Field label="Mobile Number">
                        <input
                          placeholder="Mobile number"
                          value={contact.mobileNumber}
                          onChange={(event) => handleAdditionalChange(index, "mobileNumber", event.target.value)}
                          className={inputClassName}
                        />
                      </Field>
                      <Field label="Email Address">
                        <input
                          type="email"
                          placeholder="Email address"
                          value={contact.email}
                          onChange={(event) => handleAdditionalChange(index, "email", event.target.value)}
                          className={inputClassName}
                        />
                      </Field>
                      <Field label="WhatsApp Number">
                        <input
                          placeholder="WhatsApp number"
                          value={contact.whatsappNumber}
                          onChange={(event) => handleAdditionalChange(index, "whatsappNumber", event.target.value)}
                          className={inputClassName}
                        />
                      </Field>
                      <Field label="Primary">
                        <div className="flex h-11 items-center justify-center rounded-[6px] border border-slate-300 bg-white">
                          <input
                            type="radio"
                            name="primary-contact"
                            checked={Boolean(contact.isPrimary)}
                            onChange={() =>
                              setForm((current) => ({
                                ...current,
                                additionalContacts: current.additionalContacts.map((item, itemIndex) => ({
                                  ...item,
                                  isPrimary: itemIndex === index
                                }))
                              }))
                            }
                            className="h-4 w-4 border-slate-300 text-blue-600"
                            aria-label="Primary contact"
                          />
                        </div>
                      </Field>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="h-11 rounded-[6px] bg-red-50 px-4 text-sm font-bold text-red-600 transition hover:bg-red-100"
                      >
                        Remove
                      </button>
                      <Field label="Remarks" className="md:col-span-2 xl:col-span-7">
                        <input
                          placeholder="Optional remarks"
                          value={contact.remarks}
                          onChange={(event) => handleAdditionalChange(index, "remarks", event.target.value)}
                          className={inputClassName}
                        />
                      </Field>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">You can add multiple contacts for this customer.</p>
        </FormCard>

        <FormCard title="Additional Information">
            <textarea
              name="additionalInfo"
              value={form.additionalInfo}
              onChange={handleChange}
              className={`${textareaClassName} min-h-28 resize-y`}
              placeholder="Any notes you want the team to remember"
            />
        </FormCard>

        <div className="sticky bottom-0 z-20 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="mx-auto flex max-w-7xl flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={cancelForm}
              className="h-11 rounded-[6px] border border-slate-300 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={saveDraft}
                className="h-11 rounded-[6px] border border-blue-300 bg-white px-6 text-sm font-bold text-blue-700 transition hover:bg-blue-50"
              >
                Save Draft
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`h-11 rounded-[6px] px-6 text-sm font-bold text-white shadow-sm transition ${
                  loading ? "bg-slate-400" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {loading ? "Saving..." : "Create Customer"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateCustomer;


